import shared from "../Shared/shared.module.css";
import { useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import {
  IconFileUpload,
  IconCloudUpload,
  IconFileText,
  IconTrash,
  IconAlertCircle,
  IconArrowRight,
} from "@tabler/icons-react";
import { talentProfile, talentUploadPdf } from "../../../services/talentCalls";
import { errorHandler } from "../../../utils";
import FullScreenLoader from "../../../components/Loaders/FullScreen/loader";

const Stage1 = () => {
  const [data, setData] = useState(null);
  const [file, setFile] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [load, setLoad] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const navigate = useNavigate();

  const handleDragEnter = (e) => { e.preventDefault(); e.stopPropagation(); setDragOver(true); };
  const handleDragLeave = (e) => { e.preventDefault(); e.stopPropagation(); setDragOver(false); };
  const handleDragOver = (e) => { e.preventDefault(); e.stopPropagation(); setDragOver(true); };
  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
    if (e.dataTransfer.files[0]?.type.match("application/")) {
      setFile(e.dataTransfer.files[0]);
      setError(false);
    } else {
      errorHandler("Only pdf and docx formats are allowed");
    }
  };

  const handleSubmit = async () => {
    if (!file && !data) {
      setError(true);
      return;
    }
    if (file) {
      const reader = new FileReader();
      reader.onloadend = async () => {
        const base64String = reader.result.replace("data:", "").replace(/^.+,/, "");
        const fileExtension = file.name.split(".").pop().toLowerCase();
        try {
          setLoad(true);
          const res = await talentUploadPdf({ talentResume: base64String, fileType: fileExtension });
          localStorage.setItem("extractedResumeData", JSON.stringify(res?.data?.data?.details));
          setLoad(false);
          navigate({ pathname: "/talent-onboarding", search: "stage=2" });
        } catch (err) {
          err?.response?.data
            ? errorHandler(err?.response?.data?.errors, "Error")
            : errorHandler("Network error: Something went wrong, Please check your internet connection and try again.", "Error");
          setLoad(false);
        }
      };
      reader.readAsDataURL(file);
    } else {
      navigate({ pathname: "/talent-onboarding", search: "stage=2" });
    }
  };

  const getUserProfile = async () => {
    setLoading(true);
    try {
      const res = await talentProfile();
      if (res?.data?.data?.talent?.pdfUrl) setData(res?.data?.data?.talent?.pdfUrl);
    } catch (_) {
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getUserProfile();
  }, []);

  return (
    <>
      {(loading || load) && <FullScreenLoader />}
      <div className={shared.panelHero}>
        <div className={shared.panelHeroLeft}>
          <div className={shared.panelHeroIcon}>
            <IconFileUpload size={22} />
          </div>
          <div>
            <div className={shared.panelTitle}>Upload your Resume</div>
            <div className={shared.panelSubtitle}>You can attach your CV via this field</div>
          </div>
        </div>
      </div>

      <div className={shared.panelBody}>
        <div
          className={`${shared.uploadZone} ${dragOver ? shared.uploadZoneDragOver : ""}`}
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
        >
          <input
            type="file"
            accept=".pdf,.doc,.docx"
            onChange={(e) => {
              setFile(e.target.files[0]);
              setError(false);
            }}
          />
          <div className={shared.uploadIconBox}>
            <IconCloudUpload size={24} />
          </div>
          <div className={shared.uploadText1}>Drag and drop here</div>
          <div className={shared.uploadText2}>OR</div>
          <div className={shared.uploadText1}>
            <span className={shared.uploadBrowse}>Browse files</span>
          </div>
        </div>

        {file && (
          <div className={shared.fileProgressRow}>
            <div className={shared.fileProgressIcon}>
              <IconFileText size={18} />
            </div>
            <div className={shared.fileProgressInfo}>
              <div className={shared.fileProgressName}>
                File: {file.name.length > 30 ? `${file.name.substring(0, 30)}...` : file.name}
              </div>
              <div className={shared.fileProgressBarTrack}>
                <div className={shared.fileProgressBarFill} style={{ width: "100%" }} />
              </div>
            </div>
            <div className={shared.fileProgressPct}>100%</div>
            <button className={shared.fileProgressDel} onClick={() => setFile(null)} disabled={load} aria-label="Remove file">
              <IconTrash size={15} />
            </button>
          </div>
        )}

        {data && !file && (
          <div className={shared.fileProgressRow}>
            <div className={shared.fileProgressIcon}>
              <IconFileText size={18} />
            </div>
            <div className={shared.fileProgressInfo}>
              <a href={data} target="_blank" rel="noreferrer" className={shared.fileProgressName}>
                File: Resume.pdf
              </a>
              <div className={shared.fileProgressBarTrack}>
                <div className={shared.fileProgressBarFill} style={{ width: "100%" }} />
              </div>
            </div>
            <div className={shared.fileProgressPct}>100%</div>
            <button
              className={shared.fileProgressDel}
              onClick={() => {
                setFile(null);
                setData(null);
              }}
              aria-label="Remove file"
            >
              <IconTrash size={15} />
            </button>
          </div>
        )}

        {error && (
          <div className={shared.fieldErr} style={{ marginTop: 10 }}>
            <IconAlertCircle size={14} />
            <span>Please upload your resume to continue.</span>
          </div>
        )}

        <div className={shared.uploadFormatNote}>Note: the preferred file formats are pdf and word</div>
      </div>

      <div className={shared.panelFooter}>
        <div />
        <div className={shared.footerActions}>
          <button className={shared.btnPrimary} onClick={handleSubmit} disabled={load}>
            Next <IconArrowRight size={15} />
          </button>
        </div>
      </div>
    </>
  );
};

export default Stage1;
